import {
  isAnyOf,
  ListenerEffectAPI,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit"

import { itineraryApi } from "@/lib/storage/api/itinerary-api"
import { savedPlacesApi } from "@/lib/storage/api/saved-places-api"
import { tripsApi } from "@/lib/storage/api/trips-api"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { selectHasOpenDialog } from "@/lib/storage/dialog-slice"
import { nudgeEmailVerificationIfDue } from "@/lib/storage/post-signup-thunks"
import { RootState } from "@/lib/storage/redux-store"
import userSlice, { selectStreamIds } from "@/lib/storage/user-slice"

// Long enough for a dialog the user is still reading, short enough that the
// prompt still belongs to what they just did.
const SCREEN_WAIT_MS = 30 * 1000

// Not AppDispatch: that is derived from buildStore(), which mounts this
// middleware, and the resulting cycle collapses AppDispatch to any app-wide.
type NudgeListenerApi = ListenerEffectAPI<
  RootState,
  ThunkDispatch<RootState, unknown, UnknownAction>
>

const emailVerificationMiddleware = createMtListenerMiddleware<RootState>(
  "email-verification-middleware"
)

function screenIsFree(state: RootState) {
  return !selectHasOpenDialog(state) && selectStreamIds(state).length === 0
}

// Triggers routinely land while the screen is still busy — the save dialog closes
// only after its own mutation resolves — and the nudge declines a busy screen, so
// waiting keeps those chances instead of dropping them.
async function nudgeWhenScreenIsFree(listenerApi: NudgeListenerApi) {
  if (!screenIsFree(listenerApi.getState())) {
    const free = await listenerApi.condition(
      (_action, state) => screenIsFree(state),
      SCREEN_WAIT_MS
    )
    if (!free) return
  }

  await listenerApi.dispatch(nudgeEmailVerificationIfDue())
}

// Things the user set out to do, where being asked to verify reads as settling in
// rather than as a toll on the way through.
emailVerificationMiddleware.startListening({
  matcher: isAnyOf(
    tripsApi.endpoints.createTripFromText.matchFulfilled,
    savedPlacesApi.endpoints.addSavedPlace.matchFulfilled,
    savedPlacesApi.endpoints.bulkAddSavedPlaces.matchFulfilled,
    itineraryApi.endpoints.createEvent.matchFulfilled,
    itineraryApi.endpoints.addIdeas.matchFulfilled
  ),
  effect: async (_action, listenerApi) => {
    await nudgeWhenScreenIsFree(listenerApi)
  },
})

// A message that got an answer. stopMessageStreaming is the stream's "done"
// event; the teardown right after it is identical to the teardown for a cancel,
// an error, or navigating away, so it can't stand in for an answer arriving.
emailVerificationMiddleware.startListening({
  actionCreator: userSlice.actions.stopMessageStreaming,
  effect: async (_action, listenerApi) => {
    await nudgeWhenScreenIsFree(listenerApi)
  },
})

export { emailVerificationMiddleware }
